import { ref } from 'vue';
import { useAuth0 } from '@auth0/auth0-vue';

const API_BASE = process.env.VUE_APP_API_BASE_URL || 'https://calendar-navy-eight.vercel.app';
const CACHE_KEY = 'admin_users_cache';

const users = ref([]);
const loading = ref(false);
const error = ref(null);

// Load initial state from LocalStorage immediately if available
try {
  const cached = localStorage.getItem(CACHE_KEY);
  if (cached) {
    users.value = JSON.parse(cached);
  }
} catch (e) {
  console.error('Failed to parse local user cache:', e);
}

function normalizeUser(user) {
  if (!user) return null;
  
  return {
    id: user.id || user.user_id,
    email: user.email,
    name: user.name,
    picture: user.picture,
    firstname: user.firstname || '',
    lastname: user.lastname || '',
    role: user.role || 'User',
    location: user.location || {
      address_line1: '',
      address_line2: '',
      address_line3: '',
      country: '',
      postcode: ''
    },
    logins: user.logins ?? 0,
    lastLogin: user.lastLogin || null
  };
}

export function useUserDirectory() {
  const { getAccessTokenSilently } = useAuth0();

  const getAuthHeaders = async () => {
    const token = await getAccessTokenSilently();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  };

  const fetchUsers = async () => {
    loading.value = true;
    error.value = null;

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/admin/users?_t=${Date.now()}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const payload = await response.json();
      const rawList = Array.isArray(payload) ? payload : (payload.users || payload.data || []);

      const normalized = rawList.map(normalizeUser).filter(Boolean);

      console.log(normalized);

      users.value = normalized;
      localStorage.setItem(CACHE_KEY, JSON.stringify(normalized));
    } catch (err) {
      console.error('Fetch Users Failed:', err);
      error.value = err.message || 'Failed to connect to server';
    } finally {
      loading.value = false;
    }
  };

  const saveUser = async (userData, isEditing) => {
    const headers = await getAuthHeaders();
    const method = isEditing ? 'PATCH' : 'POST';

    // Include password if creating a new user
    const payload = isEditing
    ? { 
        userId: userData.id, 
        name: userData.name, 
        picture: userData.picture, 
        role: userData.role,
        firstname: userData.firstname,
        lastname: userData.lastname,
        location: userData.location,
        age: userData.age
      }
    : { 
        email: userData.email, 
        password: userData.password, 
        name: userData.name, 
        picture: userData.picture, 
        role: userData.role 
      };

    const response = await fetch(`${API_BASE}/api/admin/users`, {
      method,
      headers,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    const savedData = await response.json().catch(() => null);

    if (isEditing) {
      const idx = users.value.findIndex(u => u.id === userData.id);
      if (idx !== -1) {
        users.value[idx] = {
          ...users.value[idx],
          ...userData,
          name: userData.name,
          picture: userData.picture
        };
      }
    } else {
      const newUser = savedData ? normalizeUser(savedData) : {
        ...userData,
        id: userData.user_id || userData.id || Math.random().toString(),
        logins: 0,
        lastLogin: null
      };
      users.value.unshift(newUser);
    }

    localStorage.setItem(CACHE_KEY, JSON.stringify(users.value));
  };


  return {
    users,
    loading,
    error,
    fetchUsers,
    saveUser,
  };
}